import {Observable, of, merge, concat} from 'rxjs';
import {flatMap, catchError} from 'rxjs/operators';
import {arrayPush, arraySplice, arrayRemove, formValueSelector} from 'redux-form';
import {List} from 'immutable';
import i18next from 'i18next';
import moment from 'moment-timezone';

import {combineEpics} from 'web-app/util/redux-observable';
import * as SessionObservables from 'web-app/react/entities/sessions/epics';
import {showDeleteDialogObservable, showDialogObservable, DialogChoice} from 'web-app/react/dialog';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import * as SessionConstants from 'web-app/react/entities/sessions/constants';

import * as Actions from './actions';
import * as Constants from './constants';
import * as SessionsApi from '../../../entities/sessions/api';
import * as Selectors from './selectors';

const fetchSessionsObservable = institutionId =>
    SessionObservables.fetchAllObservable(institutionId).flatMap(sessionAction => [
        sessionAction,
        Actions.fetchAllSessionsSuccess.action(sessionAction.payload.sessions.map(s => s.version)),
    ]);

const fetchAllSessions = action$ =>
    action$.ofType(Actions.fetchAllSessions.type).mergeMap(({payload}) => {
        return fetchSessionsObservable(payload.institutionId).catch(e =>
            Observable.of(Actions.fetchAllSessionsFailed.action(e)),
        );
    });

const addSessionEpic = action$ =>
    action$.ofType(Actions.addSession.type).flatMap(({payload}) => {
        const {successData, session} = payload;

        const allPricingGroups = List(successData.allPricingGroups);

        const sessionIdObservable = Observable.of(
            arrayPush(Constants.FORM_NAME, successData.name, {sessionId: session.sessionId}),
        );

        if (successData.count === 0) {
            let pricingGroups = session.pricing
                .groupBy(pricing => pricing.pricingGroupId)
                .map(pricingGroup => pricingGroup.sortBy(pricing => pricing.volume));

            pricingGroups = pricingGroups
                .map(pricingGroup => {
                    const firstPricingGroup = pricingGroup.first();
                    const currentPricingGroup =
                        allPricingGroups && allPricingGroups.size > 0
                            ? allPricingGroups.find(ag => ag.pricingGroupId === firstPricingGroup.pricingGroupId)
                            : [];

                    firstPricingGroup.pricingGroupTitle = currentPricingGroup ? currentPricingGroup.title : '';
                    firstPricingGroup.pricingGroupLower = currentPricingGroup ? currentPricingGroup.lower : 0;
                    return firstPricingGroup;
                })
                .sortBy(ag => ag.pricingGroupLower);

            const pricingGroupObservables = pricingGroups
                .map(pricingGroup => {
                    const pricingGroupToAdd = {
                        pricingGroupTitle: pricingGroup.pricingGroupTitle,
                        pricingGroupId: pricingGroup.pricingGroupId,
                        pricing: pricingGroup.pricing,
                        sessionPricing: List()
                            .setSize(Constants.NUMBER_OF_VOLUMES)
                            .set(0, {
                                sessionPrice: `${pricingGroup.price}`,
                                adhocPrice: pricingGroup.adhocPrice ? `${pricingGroup.adhocPrice}` : null,
                            })
                            .toJS(),
                    };

                    return Observable.of(
                        arrayPush(Constants.FORM_NAME, successData.sessionPricesName, pricingGroupToAdd),
                    );
                })
                .valueSeq()
                .toArray();

            return Observable.concat(...pricingGroupObservables, sessionIdObservable);
        } else {
            return Observable.concat(sessionIdObservable);
        }
    });

const removeSessionEpic = action$ =>
    action$.ofType(Actions.removeSessionFromGroup.type).flatMap(({payload}) => {
        const {fields, index, groupIndex} = payload;

        if (fields.length === 1) {
            return Observable.of(
                arraySplice(Constants.FORM_NAME, 'groups', groupIndex, 1, {
                    pricingGroups: [],
                }),
            );
        } else {
            return Observable.of(arrayRemove(Constants.FORM_NAME, fields.name, index));
        }
    });

const removeGroupEpic = (action$, state$) =>
    action$.ofType(Actions.removeGroupFromForm.type).flatMap(({payload}) => {
        const {group, index, validFrom, groupToDelete} = payload;

        return showDeleteDialogObservable({
            title: '',
            description: i18next.t('plans.settings.volumes.groups.confirmDeleteGroup', {
                date: moment(validFrom).format('L'),
            }),
            okText: i18next.t('remove'),
        }).mergeMap(selection => {
            if (selection === DialogChoice.OK) {
                const removeGroupObservable = Observable.of(arrayRemove(Constants.FORM_NAME, 'groups', index));

                if (group.volumeDiscountGroupId) {
                    const standaloneSessions = Selectors.convertToStandalone(state$.value, {groupToDelete, validFrom});

                    return Observable.concat(
                        removeGroupObservable,
                        SessionsApi.updateSessions(standaloneSessions)
                            .map(result => Actions.updateSessionDiscountVolumeGroupsSuccess.action(result))
                            .catch(e => Observable.of(Actions.updateSessionDiscountVolumeGroupsFailed.action(e))),
                    );
                } else {
                    return removeGroupObservable;
                }
            }

            return Observable.of(Actions.removeGroupFromFormAborted.action());
        });
    });

const addDiscountGroupToFormEpic = action$ =>
    action$.ofType(Actions.addDiscountGroupToForm.type).mergeMap(({payload}) => {
        return Observable.of(
            arrayPush(Constants.FORM_NAME, payload.name, {
                pricingGroups: [],
            }),
        );
    });

const addVolumeToGroup = (action$, state$) =>
    action$.ofType(Actions.addVolumeToGroup.type).flatMap(({payload}) => {
        const selector = formValueSelector(Constants.FORM_NAME);
        const group = selector(state$.value, payload.name);
        return group.pricingGroups.map((_, index) => {
            return arrayPush(Constants.FORM_NAME, `${payload.name}.pricingGroups[${index}].sessionPricing`, null);
        });
    });

const updateSessionDiscountVolumeGroupsEpic = (action$, state$) =>
    action$.ofType(Actions.updateSessionDiscountVolumeGroups.type).flatMap(({payload}) => {
        const savableSessions = Selectors.savableSessions(state$.value, {
            discountVolumeGroups: payload.discountVolumeGroups,
            pricingGroups: payload.pricingGroups,
        });

        if (savableSessions.length === 0) {
            return Observable.of(Actions.updateSessionDiscountVolumeGroupsAborted.action());
        }

        const updateObservable = SessionsApi.updateSessions(savableSessions).pipe(
            flatMap(result =>
                merge(
                    of(Actions.updateSessionDiscountVolumeGroupsSuccess.action(result)),
                    of(
                        GlobalEventActions.updateSuccess.action(
                            i18next.t('plans.settings.volumes.groups.volumePricingUpdated'),
                        ),
                    ),
                ),
            ),
            catchError(e => {
                if (e.errorCode === SessionConstants.RequiresForceUpdateErrorCode) {
                    return showDialogObservable({
                        title: '',
                        description: i18next.t('plans.settings.volumes.groups.confirmDelete'),
                    }).mergeMap(selection => {
                        if (selection === DialogChoice.OK) {
                            return SessionsApi.updateSessions(savableSessions, true).catch(e =>
                                Observable.of(Actions.updateSessionDiscountVolumeGroupsFailed.action(e)),
                            );
                        } else {
                            return Observable.of(Actions.updateSessionDiscountVolumeGroupsAborted.action());
                        }
                    });
                }
                return Observable.of(Actions.updateSessionDiscountVolumeGroupsFailed.action(e));
            }),
        );

        return concat(updateObservable, of(Actions.fetchAllSessions.action(payload.institutionId)));
    });

export default combineEpics(
    fetchAllSessions,
    addSessionEpic,
    removeGroupEpic,
    addDiscountGroupToFormEpic,
    addVolumeToGroup,
    removeSessionEpic,
    updateSessionDiscountVolumeGroupsEpic,
);
